import React, {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useAnchoredPopover from './useAnchoredPopover';
import './fields.scss';

/**
 * SelectField — a reusable, searchable dropdown.
 *
 * Drop-in replacement for `<Input type="select">` / `<select>`: pass either an
 * `options` array (`{ value, label, disabled? }`) or keep the existing
 * `<option>` children. `onChange` / `onBlur` receive an event-shaped object
 * `{ target: { name, id, value, type: 'select-one' } }`, so Formik's
 * `handleChange` / `handleBlur` and any `e.target.value` handler keep working.
 *
 * Like a native select, the emitted value is a string; `''` means nothing
 * selected. An `<option>` without a `value` (or with `value=""`) is treated as
 * the placeholder row, not as a selectable value.
 *
 * Usage:
 *   <SelectField
 *     name="gender"
 *     value={validation.values.gender}
 *     onChange={validation.handleChange}
 *     onBlur={validation.handleBlur}
 *     invalid={validation.touched.gender && !!validation.errors.gender}
 *   >
 *     <option value="">Select Gender...</option>
 *     <option value="Female">Female</option>
 *   </SelectField>
 *
 *   <SelectField name="wardId" options={wards.map(w => ({ value: w.id, label: w.name }))} searchable />
 */

const SEARCH_THRESHOLD = 8;

const textOf = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf(node.props.children);
  return '';
};

const isEmptyValue = (v) => v === undefined || v === null || v === '';

const optionsFromChildren = (children) => {
  const out = [];
  const walk = (nodes) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      if (child.type === Fragment) {
        walk(child.props.children);
        return;
      }
      if (child.type === 'option') {
        const label = textOf(child.props.children).trim();
        const hasValue = 'value' in child.props;
        // Native behaviour: an <option> without a value uses its label as the
        // value. A value-less FIRST option is treated as the placeholder row
        // only when its label reads like one ("Select Gender...", "Choose...").
        const looksLikePlaceholder =
          /^(select|choose|search|all\s|--)/i.test(label) || /(\.{3}|…|:)$/.test(label);
        const value = hasValue
          ? child.props.value
          : out.length === 0 && looksLikePlaceholder
          ? ''
          : label;
        out.push({
          value,
          label,
          disabled: !!child.props.disabled,
        });
        return;
      }
      if (child.type === 'optgroup') {
        walk(child.props.children);
      }
    });
  };
  walk(children);
  return out;
};

const SelectField = ({
  name,
  id,
  value,
  options,
  children,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  invalid = false,
  searchable,
  clearable = false,
  emptyText = 'No options found',
  className,
  style,
}) => {
  const fieldId = id || name;

  const { list, placeholderText } = useMemo(() => {
    const raw = options || optionsFromChildren(children);
    const placeholderOpt = raw.find((o) => isEmptyValue(o.value));
    return {
      list: raw.filter((o) => !isEmptyValue(o.value)),
      placeholderText: placeholder || placeholderOpt?.label || 'Select...',
    };
  }, [options, children, placeholder]);

  const selected = useMemo(
    () =>
      isEmptyValue(value)
        ? null
        : list.find((o) => String(o.value) === String(value)) || null,
    [list, value],
  );

  const isSearchable =
    searchable === undefined ? list.length >= SEARCH_THRESHOLD : searchable;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const wasOpen = useRef(false);

  const close = useCallback(() => setOpen(false), []);
  const popStyle = useAnchoredPopover({
    open,
    anchorRef,
    popRef,
    onClose: close,
    matchWidth: true,
    minWidth: 160,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((o) => o.label.toLowerCase().includes(q));
  }, [list, query]);

  const emit = (v) => {
    if (!onChange) return;
    onChange({
      target: {
        name,
        id: fieldId,
        value: isEmptyValue(v) ? '' : String(v),
        type: 'select-one',
      },
    });
  };

  // Mirror native behaviour: leaving the field marks it as touched.
  useEffect(() => {
    if (wasOpen.current && !open && onBlur) {
      onBlur({
        target: {
          name,
          id: fieldId,
          value: selected ? String(selected.value) : '',
          type: 'select-one',
        },
      });
    }
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const idx = selected ? list.indexOf(selected) : -1;
    setHighlight(idx >= 0 ? idx : 0);
    // focus the search box (or the list for keyboard navigation) once mounted
    const t = setTimeout(() => {
      const el = searchRef.current || popRef.current;
      if (el) el.focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  // Typing a query highlights the first match so Enter picks it; clearing the
  // query goes back to the current selection.
  useEffect(() => {
    if (!open) return;
    if (query) {
      setHighlight(filtered.length ? 0 : -1);
    } else {
      const idx = selected ? list.indexOf(selected) : -1;
      setHighlight(idx >= 0 ? idx : filtered.length ? 0 : -1);
    }
  }, [query]);

  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    // Scroll within the list only — scrollIntoView() would also scroll the page.
    const list = listRef.current;
    const el = list.children[highlight];
    if (!el) return;
    if (el.offsetTop < list.scrollTop) {
      list.scrollTop = el.offsetTop;
    } else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
    }
  }, [highlight, open]);

  const pick = (opt) => {
    if (!opt || opt.disabled) return;
    emit(opt.value);
    close();
    anchorRef.current?.focus({ preventScroll: true });
  };

  const clear = (e) => {
    e.stopPropagation();
    emit('');
  };

  const toggle = () => {
    if (disabled) return;
    setOpen((v) => !v);
  };

  const move = (delta) => {
    if (!filtered.length) return;
    let next = highlight;
    for (let i = 0; i < filtered.length; i += 1) {
      next = (next + delta + filtered.length) % filtered.length;
      if (!filtered[next].disabled) break;
    }
    setHighlight(next);
  };

  const onTriggerKeyDown = (e) => {
    if (disabled) return;
    if (['ArrowDown', 'ArrowUp'].includes(e.key) || (e.key === ' ' && !open)) {
      e.preventDefault();
      if (!open) setOpen(true);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    }
  };

  const onMenuKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Enter':
        e.preventDefault();
        pick(filtered[highlight]);
        break;
      case 'Escape':
        e.stopPropagation();
        close();
        anchorRef.current?.focus({ preventScroll: true });
        break;
      case 'Tab':
        close();
        break;
      default:
    }
  };

  return (
    <div
      className={classNames('sf-wrap', { 'is-invalid': invalid }, className)}
    >
      <button
        ref={anchorRef}
        type="button"
        id={fieldId}
        name={name}
        disabled={disabled}
        style={style}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onTriggerKeyDown}
        className={classNames('form-control sf-field', {
          'is-invalid': invalid,
          'sf-field--clearable': clearable && selected,
        })}
      >
        <span className="sf-field__text">
          {selected ? (
            selected.label
          ) : (
            <span className="sf-field__placeholder">{placeholderText}</span>
          )}
        </span>
        <i
          className={classNames('mdi mdi-chevron-down sf-field__icon', {
            'sf-field__icon--open': open,
          })}
        />
      </button>
      {clearable && selected && !disabled && (
        <button
          type="button"
          aria-label="Clear selection"
          className="sf-field__clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={clear}
        >
          <i className="mdi mdi-close" />
        </button>
      )}

      {open &&
        createPortal(
          <div
            ref={popRef}
            tabIndex={-1}
            className="sf-pop sf-menu"
            style={popStyle}
            onKeyDown={onMenuKeyDown}
          >
            {isSearchable && (
              <div className="sf-menu__search">
                <i className="mdi mdi-magnify" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search..."
                  value={query}
                  autoComplete="off"
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
            <ul ref={listRef} role="listbox" className="sf-menu__list">
              {filtered.length === 0 && (
                <li className="sf-menu__empty">{emptyText}</li>
              )}
              {filtered.map((opt, idx) => {
                const isSel = selected && String(opt.value) === String(selected.value);
                return (
                  <li
                    key={`${String(opt.value)}-${idx}`}
                    role="option"
                    aria-selected={!!isSel}
                    aria-disabled={opt.disabled || undefined}
                    className={classNames('sf-menu__opt', {
                      'sf-menu__opt--hi': idx === highlight,
                      'sf-menu__opt--sel': isSel,
                      'sf-menu__opt--dis': opt.disabled,
                    })}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(opt)}
                  >
                    <span className="sf-field__text">{opt.label}</span>
                    {isSel && <i className="mdi mdi-check" />}
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
};

SelectField.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node,
      disabled: PropTypes.bool,
    }),
  ),
  children: PropTypes.node,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  searchable: PropTypes.bool,
  clearable: PropTypes.bool,
  emptyText: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default SelectField;
